// Lightbox.jsx
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { downloadImage } from "../../services/firebaseService/endPoints/clubAdmin/gallery";
import ChantPopConfirm from "../ChantTooltip/ChantPopConfirm";
import ChantSpinner from "../ChantSpinner/ChantSpinner";

// Custom hook to track image loading state
const useImageLoading = (src) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    img.onload = () => setIsLoading(false);
    img.onerror = () => {
      setIsLoading(false);
      setHasError(true);
    };
    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return { isLoading, hasError };
};

const Lightbox = ({
  images,
  lightboxOpen,
  setLightboxOpen,
  setCurrentImageIndex,
  currentImageIndex,
  deletingImageId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});

  // Ensure currentImageIndex is within bounds
  const safeIndex = Math.min(currentImageIndex, images.length - 1);
  const currentImageSrc = images[safeIndex]?.srcLg || images[safeIndex]?.src;

  // Use the custom hook to track current image loading
  const { isLoading: imageLoading, hasError: imageError } =
    useImageLoading(currentImageSrc);

  useEffect(() => {
    if (!lightboxOpen) return;

    const preloadImage = (index) => {
      const imageToLoad = images[index]?.srcLg || images[index]?.src;
      if (!imageToLoad || loadedImages[imageToLoad]) return;

      const img = new Image();
      img.src = imageToLoad;
      img.onload = () => {
        setLoadedImages((prev) => ({ ...prev, [imageToLoad]: true }));
      };
    };

    const nextIndex =
      currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1;
    const prevIndex =
      currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;

    preloadImage(nextIndex);
    preloadImage(prevIndex);
  }, [currentImageIndex, images, lightboxOpen, loadedImages]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!lightboxOpen) return;

      switch (e.key) {
        case "ArrowLeft":
          navigateImages("prev");
          break;
        case "ArrowRight":
          navigateImages("next");
          break;
        case "Escape":
          setLightboxOpen(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, setLightboxOpen, setCurrentImageIndex, images.length]);

  const navigateImages = (direction) => {
    if (images.length === 0) return; // No images to navigate

    setIsLoading(true);
    if (direction === "next") {
      setCurrentImageIndex((prev) =>
        prev === images.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === 0 ? images.length - 1 : prev - 1
      );
    }
    setTimeout(() => setIsLoading(false), 100);
  };

  if (!lightboxOpen) return null;

  // Close lightbox if no images
  if (images.length === 0) {
    setLightboxOpen(false);
    return null;
  }

  const handleDownload = () => {
    downloadImage(currentImageSrc);
  };

  return (
    <div className="lightbox">
      {deletingImageId !== images[safeIndex]?.id && (
        <div className="lightbox-actions">
          <button onClick={handleDownload} className="lightbox-download">
            <DownloadOutlined />
          </button>

          <ChantPopConfirm
            title="Delete image?"
            onConfirm={(e) => {
              e.stopPropagation();
              if (images[safeIndex]?.onDelete) {
                images[safeIndex].onDelete();
                setLightboxOpen(false); // Close lightbox after deletion
              }
            }}
            onCancel={(e) => e.stopPropagation()}
            okButtonProps={{
              loading: deletingImageId === images[safeIndex]?.id,
              disabled: deletingImageId === images[safeIndex]?.id,
            }}
            cancelButtonProps={{
              disabled: deletingImageId === images[safeIndex]?.id,
            }}
            okText="Yes"
            cancelText="No"
          >
            <button className="lightbox-delete">
              <DeleteOutlined />
            </button>
          </ChantPopConfirm>
        </div>
      )}
      <button onClick={() => setLightboxOpen(false)} className="lightbox-close">
        <CloseOutlined />
      </button>

      <button
        onClick={() => navigateImages("prev")}
        className="lightbox-nav lightbox-prev"
      >
        <LeftOutlined />
      </button>

      <div className="lightbox-image-container">
        {isLoading ||
        imageLoading ||
        deletingImageId === images[safeIndex]?.id ? (
          <ChantSpinner isScreenLoader />
        ) : (
          <img
            key={currentImageSrc}
            src={currentImageSrc}
            alt={`expanded view ${safeIndex + 1}`}
            className={`lightbox-image ${isLoading ? "loading" : ""}`}
            style={{ display: imageError ? "none" : "block" }}
          />
        )}
        {imageError && (
          <div className="lightbox-error">
            <p>Failed to load image</p>
          </div>
        )}
      </div>

      <button
        onClick={() => navigateImages("next")}
        className="lightbox-nav lightbox-next"
      >
        <RightOutlined />
      </button>
    </div>
  );
};

export default Lightbox;
